import {
  getRandomStr,
  propIsFalse,
  propIsTrue,
  isNotEmpty,
  changeArrayItemIndex,
  removeUndefinedProperties,
} from '@pb/utils'
import { differenceInYears } from 'date-fns'
import {
  mergeDeepRight,
  when,
  filter,
  drop,
  toPairs,
  flatten,
  union,
  mergeWithKey,
  groupBy,
  prop,
  concat,
  merge,
  pick,
  omit,
  keys,
  map,
  head,
  reduce,
  path,
  curry,
  pathOr,
  compose,
  addIndex,
} from 'ramda'

import { User } from 'App/duck/types/user'
import { findDeliveryAddress } from 'features/checkout/helpers'
import { groupByProp } from 'helpers/cookbook'
import { splitDateOfBirth } from 'helpers/dateOfBirth'
import { ThunkResult } from 'redux/types'
import { ApiError } from 'types'

import { DELIVERY_METHODS, CHECKOUT_ERROR_CODES, DROPSHIP_TYPE } from '../constants'
import {
  Address,
  NormalizedDelivery,
  NormalizedDeliveryModes,
  Delivery,
  DeliveryMode,
  PickupPlace,
  NormalizedPickupPlace,
  DeliveryMethods,
  Prepare,
  NormalizedPrepare,
  CartState,
  Context,
  NormalizedContext,
  CheckoutSettings,
  NormalizedCheckoutSettings,
  AddressValidateResponseData,
  ConsignmentDeliveryData,
  PaymentState,
  Customer,
  CustomerState,
  Item,
} from '../types'

const mapWithIndex = addIndex(map)
// normalizeUser :: map context.customer to user
export const normalizeUser = (customer: Customer): Partial<User> => {
  const { customerId, type, loyaltyStatus } = customer
  const userAttributesFromCustomer = { customerId, customerType: type, status: loyaltyStatus }
  const userAttributesToOverride = removeUndefinedProperties(userAttributesFromCustomer)

  return userAttributesToOverride
}
// addAdditionalDataIntoPickupPlaces
// * add deliveryModes property as Array to know what delivery modes is available for the specific pickup point
// * add index of PuP within delivery mode, it is needed for sorting which is based on distance + index
const addAdditionalDataIntoPickupPlaces: any = (code) =>
  compose(
    drop(1),
    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
    map(mapWithIndex((val, index) => merge({ deliveryModes: [head(code)], index }, val))),
  )(code)
// mergePUPs :: merge multiple pickup points and save delivery modes as a set ( union will remove duplicates as a set )
const mergePickupPlaces: any = (codes, code) =>
  mergeWithKey(
    (key, left, right) => (key === 'deliveryModes' ? union(left, right) : left),
    codes,
    code,
  )

// Address format is different in PUP list response
const normalizePickupPlace = ({
  postcode,
  townCity,
  ...pickupPlace
}: PickupPlace): NormalizedPickupPlace => ({
  ...pickupPlace,
  postalCode: postcode,
  town: townCity,
})

// denormalizePUP :: switch back to the original keys for PATCH DELIVERY request
export const denormalizePickupPlace = ({
  postalCode,
  town,
  ...pickupPlace
}: NormalizedPickupPlace): PickupPlace => ({
  ...pickupPlace,
  postcode: postalCode,
  townCity: town,
})
/**
 * normalizePickupPoints ::
 * - add deliveryModes property as Array to know what delivery modes is available for the specific pickup point
 * - merge multiple pickup points and save delivery modes as a set ( union will remove duplicates as a set )
 * - group by code
 * - remove not available pickup points ( available=false )
 */
export const normalizePickupPlaces = compose(
  map(normalizePickupPlace),
  map(reduce(mergePickupPlaces, [])),
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '<T>(obj: Record<"code", T>) => T... Remove this comment to see the full error message
  groupBy(prop('code')),
  filter(propIsTrue('available')),
  flatten,
  map(addAdditionalDataIntoPickupPlaces),
  // @ts-expect-error ts-migrate(2554) FIXME: Expected 1-6 arguments, but got 7.
  toPairs,
) as (pickupPlaces: PickupPlace[]) => Record<string, NormalizedPickupPlace>

export const normalizeCustomerDetails: any = (customerDetails) => {
  // omitLoyaltyWhenFalse :: remove loyalty: false and only send loyalty: true
  const omitLoyaltyWhenFalse = when(propIsFalse('loyalty'), omit(['loyalty']))
  const customerFields = [
    'email',
    'password',
    'fashionNews',
    'privacyPolicy',
    'loyalty',
    'vatNumber',
  ]
  const customer = compose(omitLoyaltyWhenFalse, pick(customerFields))(customerDetails)

  const dateOfBirth = splitDateOfBirth(prop('dateOfBirth', customerDetails))
  const fieldsToOmit = concat(['dateOfBirth', 'title'], customerFields)
  // title :: BE will respond with error when req body contains title with empty string value { title: '' }
  const title = customerDetails?.title &&
    customerDetails.title !== '' && { title: customerDetails.title }
  const address = merge({ ...dateOfBirth, ...title }, omit(fieldsToOmit, customerDetails))
  return {
    // @ts-expect-error ts-migrate(2698) FIXME: Spread types may only be created from object types... Remove this comment to see the full error message
    ...customer,
    ...(isNotEmpty(address) && { address }),
  }
}

export const normalizeCustomer = (customer: Partial<Customer>): Partial<CustomerState> => {
  const { omnicredit, type, contactEmail, year, month, day } = customer
  const userHasAgeParameters = year && month && day
  const age = userHasAgeParameters
    ? differenceInYears(
        new Date(),
        new Date(parseInt(year, 10), parseInt(month, 10), parseInt(day, 10)),
      )
    : undefined
  let dateOfBirth = ''
  if (userHasAgeParameters) {
    dateOfBirth = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  }

  const { customerAddresses, ...customerWithoutAddress } = customer

  // Set line 2 field as '' if undefined to prevent value retaining from old state due to deep copy
  if (customerAddresses) {
    Object.keys(customerAddresses).forEach((key) => {
      customerAddresses[key].line2 = customerAddresses[key].line2 || ''
    })
  }

  const normalizedCustomerObject = {
    ...customerWithoutAddress,
    dateOfBirth,
    email: contactEmail,
    ...(customerAddresses && { currentAddresses: groupByProp('id')(customerAddresses) }),
    age,
    klarnaCustomer: omnicredit,
    type,
  }
  return removeUndefinedProperties(normalizedCustomerObject)
}

export const normalizePrepare = (prepare: Prepare): NormalizedPrepare => {
  const outOfStockItems = prop('oosItems', prepare)

  return {
    outOfStockItems,
  }
}

export const normalizeSettings = (settings: CheckoutSettings): NormalizedCheckoutSettings => {
  const deliveryModes = {}
  const paymentModes = {}
  settings.deliverySettings.zoneDeliveryModes.forEach((mode) => {
    deliveryModes[mode.code] = mode
  })

  settings.paymentSettings.paymentModes.forEach((mode) => {
    paymentModes[mode.code] = mode
  })
  return {
    deliverySettings: {
      zoneDeliveryModes: deliveryModes,
    },
    paymentSettings: {
      paymentModes,
    },
    generalSettings: settings.generalSettings,
  }
}

type NormalizeDeliveryModesInput = Pick<
  NormalizedDelivery,
  'selectedDeliverySpeed' | 'previouslyUsedPickupPlace'
> &
  Pick<Delivery, 'deliveryModeList'> & { deliveryAddress?: Pick<Address, 'id'> }

export const normalizeDeliveryModes = ({
  selectedDeliverySpeed,
  deliveryModeList,
  previouslyUsedPickupPlace,
  deliveryAddress,
}: NormalizeDeliveryModesInput): NormalizedDeliveryModes => {
  const deliveryModesByCode = groupByProp('code')(deliveryModeList)
  const selectedDeliveryMode = selectedDeliverySpeed
    ? deliveryModesByCode[selectedDeliverySpeed]
    : undefined
  const selectedItemIndex = deliveryModeList.findIndex(
    (mode: DeliveryMode) => selectedDeliverySpeed === mode.code,
  )
  // move selected to top of the list
  const sortedDeliveryModeList: DeliveryMode[] =
    selectedItemIndex > -1
      ? changeArrayItemIndex(selectedItemIndex, 0, deliveryModeList)
      : deliveryModeList
  const availableDeliveryMethods = compose(
    keys,
    groupBy<DeliveryMode>(prop('type')),
  )(deliveryModeList)
  const getMinPrice = curry((price, previousPrice) => {
    if (!previousPrice) {
      return price
    }
    return price.value < previousPrice.value ? price : previousPrice
  })
  // TODO: fix return type DeliveryMethods
  const deliveryMethods = reduce((acc: DeliveryMethods | {}, deliveryMode: DeliveryMode): any => {
    const deliveryModes = pathOr([], [deliveryMode.type, 'deliveryModes'], acc)
    const code = pathOr('', ['code'], deliveryMode)
    const lastSelectedStoreCode = path(['code'], previouslyUsedPickupPlace)
    const getMinPriceFromDeliveryMode = compose(
      getMinPrice(deliveryMode.deliveryCost),
      path([deliveryMode.type, 'minPrice']),
    )
    const isSelectedMethod = selectedDeliveryMode?.type === deliveryMode.type

    return {
      ...acc,
      [deliveryMode.type]: {
        ...(deliveryMode.type === DELIVERY_METHODS.pickupPoint &&
          isSelectedMethod &&
          previouslyUsedPickupPlace?.available && {
            selectedPickupPoint: lastSelectedStoreCode,
            // @ts-expect-error ts-migrate(2464) FIXME: A computed property name must be of type 'string',... Remove this comment to see the full error message
            pickupPoints: { [lastSelectedStoreCode]: previouslyUsedPickupPlace },
            rawPickupPointsResponse: previouslyUsedPickupPlace?.deliveryModes?.reduce(
              (modes, mode) => ({ ...modes, [mode]: [previouslyUsedPickupPlace] }),
              {},
            ),
          }),
        ...(deliveryMode.type === DELIVERY_METHODS.clickAndCollect &&
          isSelectedMethod &&
          previouslyUsedPickupPlace?.available && {
            selectedCnCPoint: lastSelectedStoreCode,
            // @ts-expect-error ts-migrate(2464) FIXME: A computed property name must be of type 'string',... Remove this comment to see the full error message
            CnCPoints: { [lastSelectedStoreCode]: previouslyUsedPickupPlace },
          }),
        minPrice: getMinPriceFromDeliveryMode(acc),
        deliveryModes: concat(deliveryModes, [code]),
        ...(deliveryMode.type !== DELIVERY_METHODS.pickupPoint &&
          isSelectedMethod &&
          deliveryAddress && {
            selectedDeliveryAddress: deliveryAddress.id,
          }),
      },
    }
  }, {})(deliveryModeList)

  return {
    // TODO: only store uniq facts, remove 1 of these deliveryModes or create ref to each other
    // this will be handled when cleaning up 1 of the variations from merged delivery section exp.
    deliveryModes: deliveryModesByCode,
    deliveryModesAsArray: sortedDeliveryModeList,
    deliveryMethods,
    availableDeliveryMethods: [...availableDeliveryMethods],
  }
}

export const normalizeDelivery = (delivery: Delivery, cartItems: Item[]): NormalizedDelivery => {
  const {
    validated,
    validatedMainConsignment,
    lastSelectedStore,
    phoneNumber,
    selectedDeliveryModeData,
    consignmentDeliveryDatas = [],
  } = delivery
  const semiBulkyItemList = cartItems
    ? cartItems?.filter((item) => item.semibulky).map((item) => item.variantCode)
    : []
  const nonSortableItemList = cartItems
    ? cartItems?.filter((item) => item.nonSortable).map((item) => item.variantCode)
    : []
  const parcels: ConsignmentDeliveryData[] = consignmentDeliveryDatas
    .map(({ consignmentData, deliveryMode, deliveryAddress, additionalStoreInfo }) => {
      const tmpObject = {}
      if (consignmentData.consignmentType === DROPSHIP_TYPE) {
        consignmentData.entries.forEach((entry) => {
          if (Object.keys(tmpObject).includes(entry.brand)) {
            tmpObject[entry.brand] = [...tmpObject[entry.brand], entry]
          } else {
            tmpObject[entry.brand] = [entry]
          }
        })
        return Object.keys(tmpObject).map((key) => {
          return {
            consignmentData: { ...consignmentData, entries: tmpObject[key] },
            deliveryMode,
            deliveryAddress,
          }
        })
      }

      // Split parcels for Semi-bulky and Non-sortable items
      let isGroupedParcel = false
      consignmentData.entries.forEach((entry) => {
        if (
          (semiBulkyItemList.includes(entry.variantCode) &&
            consignmentData.semibulkySplitEnabled) ||
          (nonSortableItemList.includes(entry.variantCode) &&
            consignmentData.nonSortableSplitEnabled)
        ) {
          tmpObject[entry.variantCode] = [entry]
          isGroupedParcel = true
        } else {
          tmpObject[entry.brand] = tmpObject[entry.brand]
            ? [...tmpObject[entry.brand], entry]
            : [entry]
        }
      })
      return Object.keys(tmpObject).map((key) => {
        return {
          consignmentData: { ...consignmentData, entries: tmpObject[key] },
          deliveryMode,
          deliveryAddress,
          additionalStoreInfo,
          isGroupedParcel,
        }
      })
    })
    .flat()

  const previouslyUsedPickupPlace = lastSelectedStore && normalizePickupPlace(lastSelectedStore)
  const selectedDeliveryType = selectedDeliveryModeData?.type
  const selectedDeliverySpeed = consignmentDeliveryDatas?.[0]?.deliveryMode?.code
  const selectedTimeSlot = consignmentDeliveryDatas?.[0]?.deliveryMode?.selectedTimeSlot
  return {
    validated,
    ...(validatedMainConsignment && { validatedMainConsignment }),
    ...(selectedDeliveryType && {
      selectedDeliveryMethod: selectedDeliveryType,
    }),
    selectedDeliveryModeData,
    selectedDeliverySpeed,
    phoneNumber,
    ...(lastSelectedStore?.available && {
      previouslyUsedPickupPlace,
    }),
    splitOrder: {
      parcels: parcels?.map((parcel, i) => ({
        ...parcel,
        id: `parcel-id-${i}`,
        deliveryAddress: parcel?.deliveryAddress,
        ...(parcel?.additionalStoreInfo && { additionalStoreInfo: parcel?.additionalStoreInfo }),
      })),
    },
    consignmentDatas: consignmentDeliveryDatas.map((consignment) => consignment.consignmentData),
    selectedTimeSlot,
  }
}

const normalizeSavedCard: any = (paymentInfo) => ({
  ...paymentInfo,
  lastFourDigits: paymentInfo?.cardNumber?.substr(-4),
})

const normalizePaymentData: any = (payment) => ({
  ...(payment.adyenData && {
    adyenPaymentData: payment.adyenData,
  }),
  ...(payment.cybersourceData && {
    cybersourcePaymentData: {
      keyId: payment.cybersourceData.keyId,
    },
  }),
  ...(payment.omniCreditData && {
    klarnaData: {
      klarnaClientToken: payment.omniCreditData.clientToken,
      updateSessionBodyObj: payment.omniCreditData.updateSessionBodyObj,
      omnicreditAuthCallbackEnabled: payment.omniCreditData.omnicreditAuthCallbackEnabled,
    },
  }),
})

const normalizePayment = (payment): Partial<PaymentState> => {
  const normalizedPaymentValues = {
    availablePaymentMethods: payment.paymentModeList,
    selectedPaymentMethod: payment.selectedPaymentMode,
    validated: payment.validated,
    validatedPayment: payment.validatedPayment,
    validatedBilling: payment.validatedBilling,
    paymentUserInputNeeded: payment.paymentUserInputNeeded,
    giftCardList: payment.giftCardList,
    isPurchaseCoveredByGiftCards: payment.giftCardGTtotalPrice,
    savedCards: (payment.paymentInfoList || []).map(normalizeSavedCard),
    selectedPaymentCardId: payment.selectedPaymentInfo,
    cardholderAddress: payment.newPaymentInfo?.address,
    adyenApplePayPaymentMethodData: payment.adyenApplePayPaymentMethodData,
    preSelectedBankId: payment.preSelectedBankId,
  }

  return removeUndefinedProperties(normalizedPaymentValues)
}

const normalizePaymentError = ({ header, message }: Partial<ApiError>): ApiError => ({
  code: CHECKOUT_ERROR_CODES.paymentError,
  header,
  message,
})

const normalizeCart = (cart): CartState => {
  if (cart?.deliveryAddress && !cart.deliveryAddress?.line2) {
    cart.deliveryAddress.line2 = ''
  }
  if (cart?.billingAddress && !cart.billingAddress?.line2) {
    cart.billingAddress.line2 = ''
  }
  return {
    ...cart,
    ...(cart.deliveryAddress ? { deliveryAddress: cart.deliveryAddress } : {}),
    ...(cart.billingAddress ? { billingAddress: cart.billingAddress } : {}),
  }
}

// TODO will fix any once we migrate to V2
export const normalizeContext = ({
  cart,
  customer,
  delivery,
  payment,
}: any = {}): NormalizedContext => {
  const { paymentError, cybersourceData, omniCreditData, ...restPayment } = payment || {}
  // TODO fix it when we move completely V2
  const adyenData = payment?.adyenData ? payment?.adyenData : payment?.adyenPaymentData
  return {
    ...(cart && { cart: normalizeCart(cart) }),
    ...(customer && { customer: normalizeCustomer(customer) }),
    ...(delivery && {
      delivery: normalizeDelivery(delivery, cart?.items),
    }),
    ...(payment && { payment: normalizePayment(restPayment) }),
    ...(payment && {
      paymentData: normalizePaymentData({ adyenData, cybersourceData, omniCreditData }),
    }),
    ...(paymentError && {
      errors: [normalizePaymentError(paymentError)],
    }),
  }
}

export const normalizeContextPartial =
  ({ cart, customer, delivery, payment }: Partial<Context>): ThunkResult<NormalizedContext> =>
  (_, getState) => {
    const {
      paymentError,
      adyenPaymentData: adyenData,
      cybersourceData,
      omniCreditData,
      ...restPayment
    } = payment || {}
    const currentState = getState()
    const currentContext = currentState.checkout
    const contextToOverride = {
      ...(cart && { cart: normalizeCart(cart) }),
      ...(customer && { customer: normalizeCustomer(customer) }),
      ...(delivery && {
        delivery: normalizeDelivery(delivery, cart?.items || currentContext.cart?.items),
      }),
      ...(payment && { payment: normalizePayment(restPayment) }),
      ...(payment && {
        paymentData: normalizePaymentData({ adyenData, cybersourceData, omniCreditData }),
      }),
      ...(paymentError && {
        errors: [normalizePaymentError(paymentError)],
      }),
    }

    return mergeDeepRight(currentContext, contextToOverride) as NormalizedContext
  }

export const normalizeInitContext = (context: Context): NormalizedContext => {
  // need to normalize the delivery modes to be able to show
  // the delivery info in delivery overview. Otherwise we would
  // need to wait for the delivery-modes api and this creates
  // some layout shifting when it finally responds!
  const normalizedContext = normalizeContext(context)
  const { lastSelectedStore, selectedDeliveryMode: selectedDeliverySpeed } = context.delivery
  const deliveryModeList = pathOr([], ['delivery', 'deliveryModeList'])(context)
  const previouslyUsedPickupPlace = lastSelectedStore && normalizePickupPlace(lastSelectedStore)
  const deliveryAddress = <Address | undefined>(
    findDeliveryAddress(normalizedContext?.customer?.currentAddresses)
  )

  const normalizedDeliveryModes = normalizeDeliveryModes({
    selectedDeliverySpeed,
    deliveryModeList,
    previouslyUsedPickupPlace,
    deliveryAddress,
  })

  // @ts-expect-error
  return mergeDeepRight(normalizedContext, { delivery: normalizedDeliveryModes })
}

export const normalizeAddressSuggestionsList: any = (addressSuggestionsList: Address[]) => {
  const addressSuggestionsWithIds = addressSuggestionsList.map((address) => ({
    ...address,
    id: getRandomStr(),
  }))
  const validatedAddresses = addressSuggestionsWithIds.filter(
    (addressSuggestion) => addressSuggestion.validated,
  )
  const enteredAddress = addressSuggestionsWithIds.find(
    (addressSuggestion) => !addressSuggestion.validated,
  )
  return {
    validatedAddresses,
    enteredAddress,
  }
}

// TODO Need to remove this any
export const normalizeAddressOperationResponseV2: any = (
  addressOperationResponse: AddressValidateResponseData,
) => {
  const addressSuggestionsList = addressOperationResponse.addressSuggestions
  if (addressSuggestionsList) {
    const AddressSuggestions = normalizeAddressSuggestionsList(addressSuggestionsList)
    return AddressSuggestions
  }
  return undefined
}
